/* eslint-disable prefer-destructuring */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { debounce } from '@mui/material/utils';
import { GridFilterItem } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { MutableRefObject, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { usePage } from '../../../contexts/usePageContext';
import { setConfigStorageItem } from '../../../hooks/useConfigStorage';
import { useCurrentStructure } from '../../../hooks/useCurrentStructure';
import { changeCurrentPreset } from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { RootState } from '../../../redux/store';
import { FormValues } from '../../../types/types';
import { findStructure } from '../../../utils/dataTypes';
import { CustomGridApi } from '../XTable/types';

const useFilterSidebar = ({
 apiRef,
}: {
 apiRef: MutableRefObject<CustomGridApi>;
}) => {
 const { pageName } = usePage();
 const dispatch = useDispatch();
 //  const allColumns = apiRef?.current?.getAllColumns();

 const currentColumnsPreset = useSelector(
  (state: RootState) =>
   state.clientConfiguration.currentColumnsPreset?.[pageName] || null,
 );
 const methods = useForm<Record<string, any>>();

 const structure = useCurrentStructure();

 const columns = apiRef?.current?.getAllColumns(); // useMemo(() => apiRef?.current?.getAllColumns(), [apiRef.current]);

 const isFormChanged = methods.formState.isDirty;

 const getValuesFromStorage = () => {
  const presetFields = currentColumnsPreset?.filters?.fields || {};
  const normalizedFields = Object.entries(presetFields).reduce<Record<string, any>>(
   (acc, [fieldKey, fieldValue]) => {
    const currentStructure = findStructure(structure, fieldKey);

    if (currentStructure?.Type === 'datetime') {
     const dateString = typeof fieldValue === 'string' ? fieldValue : '';
     const [dateFromRaw, dateToRaw] = dateString ? dateString.split('-') : ['', ''];
     if (!acc.dateRanges) {
      acc.dateRanges = {};
     }
     acc[fieldKey] = dateString;
     acc.dateRanges[fieldKey] = {
      from: dateFromRaw && dayjs(dateFromRaw).isValid() ? dayjs(dateFromRaw) : '',
      to: dateToRaw && dayjs(dateToRaw).isValid() ? dayjs(dateToRaw) : '',
      period: '',
      year: '',
      month: '',
      week: '',
     };
     return acc;
    }

    acc[fieldKey] = fieldValue || (currentStructure?.Type === 'enum' ? [] : '');
    return acc;
   },
   {},
  );

  return {
   ...normalizedFields,
   operators: currentColumnsPreset?.filters?.operators || {},
  };
 };
 function fieldsWithDots(obj: Record<string, any>): Record<string, any> {
  // https://github.com/react-hook-form/react-hook-form/issues/3351
  const out: { [key: string]: any } = {};

  function walk(x: Record<string, any>, prefix: string[] = []): void {
   if (x instanceof dayjs) {
    out[[...prefix].join('.')] = x;
   } else {
    Object.entries(x).forEach(([key, val]) => {
     if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      walk(val, [...prefix, key]);
     } else {
      out[[...prefix, key].join('.')] = val;
     }
    });
   }
  }

  walk(obj);
  return out;
 }
 const applyFilters = (data: FormValues) => {
  const { operators = {}, dateRanges = {}, ...fields } = data;
  const fixedOperators = fieldsWithDots(operators);
  const fixedFields = fieldsWithDots(fields);

  const allFilters = Object.entries(fixedFields).map(([key, value]) => {
   if (typeof value === 'undefined')
    return null;

   const currentStructure = findStructure(structure, key);
   if (currentStructure?.Type === 'datetime') return null;

   return {
    field: key,
    operator: fixedOperators[key],
    value,
   };
  }) as GridFilterItem[];

  const activeFilters = allFilters.filter(
   (f) => f !== null && f.value && f.operator !== '', // and value?
  );

  const datetimeFieldKeys = Object.keys(fixedFields).filter(
   (fieldKey) => findStructure(structure, fieldKey)?.Type === 'datetime',
  );
  datetimeFieldKeys.forEach((fieldKey) => {
   const dateFrom = dateRanges?.[fieldKey]?.from;
   const dateTo = dateRanges?.[fieldKey]?.to;

   if (dateFrom && dayjs(dateFrom).isValid()) {
    activeFilters.push({
     field: fieldKey,
     operator: 'onOrAfter',
     value: dayjs(dateFrom).toDate(),
    });
   }

   if (dateTo && dayjs(dateTo).isValid()) {
    activeFilters.push({
     field: fieldKey,
     operator: 'onOrBefore',
     value: dayjs(dateTo).toDate(),
    });
   }
  });
  console.log('activeFilters', activeFilters);
  // send to datagrid
  apiRef.current.setFilterModel(
   {
    items: activeFilters.map((item) => ({
     ...item,
     id: `${item.field}-${JSON.stringify(item.value).replace(/[^a-zA-Z0-9]/g, '')}`,
    })),
   },
   'upsertFilterItems',
  );

  const newPresetFieldsObj = Object.keys(fixedFields).reduce<Record<string, any>>(
   (acc, fieldKey) => {
    const currentStructure = findStructure(structure, fieldKey);

    if (currentStructure?.Type === 'datetime') {
      const dateFrom = dateRanges?.[fieldKey]?.from;
      const dateTo = dateRanges?.[fieldKey]?.to;
      if (!dateFrom && !dateTo) {
       acc[fieldKey] = '';
       return acc;
      }

      const fromValue =
       dateFrom && dayjs(dateFrom).isValid()
        ? dayjs(dateFrom).format('YYYY.MM.DD HH:mm:ss')
        : 'x';
      const toValue =
       dateTo && dayjs(dateTo).isValid()
        ? dayjs(dateTo).format('YYYY.MM.DD HH:mm:ss')
        : 'x';

      acc[fieldKey] = `${fromValue}-${toValue}`;
      return acc;
    }

    acc[fieldKey] = fixedFields[fieldKey];
    return acc;
   },
   {},
  );

  console.log(newPresetFieldsObj);

  const preset = {
   ...currentColumnsPreset,
   name: currentColumnsPreset?.name || '',
   columns: currentColumnsPreset?.columns || [],
   filters: { fields: newPresetFieldsObj, operators: fixedOperators },
  };

  console.log('applyFilters', preset);

  dispatch(
   changeCurrentPreset({
    page: pageName,
    type: 'columns',
    preset,
    temp: true,
   }),
  );
  dispatch(
   changeCurrentPreset({
    page: pageName,
    type: 'columns',
    preset,
    temp: false,
   }),
  );
  // dispatch(getCurrentPresetStart({ table: pageName, type: 'columns' }));

  setConfigStorageItem({
   page: pageName,
   key: 'columns',
   value: preset,
  });

  //   handleClose(false);
 };

 const resetFilters = () => {
  const presetFieldKeys = Object.keys(currentColumnsPreset?.filters?.fields || {});
  const resetFields = presetFieldKeys.reduce<Record<string, any>>((acc, fieldKey) => {
   acc[fieldKey] = '';
   return acc;
  }, {});
  const resetOperators = presetFieldKeys.reduce<Record<string, string>>(
   (acc, fieldKey) => {
    acc[fieldKey] = '';
    return acc;
   },
   {},
  );
  const resetDateRanges = presetFieldKeys.reduce<Record<string, any>>(
   (acc, fieldKey) => {
    if (findStructure(structure, fieldKey)?.Type !== 'datetime') return acc;

    acc[fieldKey] = {
     from: '',
     to: '',
     period: '',
     year: '',
     month: '',
     week: '',
    };
    return acc;
   },
   {},
  );

  const resetValues = {
   ...resetFields,
   operators: resetOperators,
   dateRanges: resetDateRanges,
  };

  methods.reset(resetValues);
  applyFilters(resetValues);
 };

 function getValue<T extends Record<string, any>>(obj: T, path: string): any {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj as any);
 }

 useEffect(() => {
  // activate operator if value is not empty and remove if
  const debouncedHandler = debounce((value, { name, type }) => {
   if (name && !name.includes('operators') && !name.includes('dateRanges')) {
    const currentValue = name.includes('.')
     ? getValue(value, name)
     : value?.[name];
    console.log(currentValue, name);
    // is empty string, or empty object, or empty array
    const isEmpty =
     currentValue === undefined ||
     currentValue === '' ||
     (!!currentValue &&
      !Array.isArray(currentValue) &&
      typeof currentValue === 'object' &&
      Object.keys(currentValue).length === 0) ||
     (Array.isArray(currentValue) && currentValue?.length === 0);

    // console.log(
    //  currentValue === undefined,
    //  currentValue === '',
    //  !!currentValue &&
    //   !Array.isArray(currentValue) &&
    //   typeof currentValue === 'object' &&
    //   Object.keys(currentValue).length === 0,
    //  Array.isArray(currentValue) && currentValue?.length === 0,
    // );

    // #########################################################
    // REWRITE THIS
    // #########################################################

    // console.log(typeof currentValues, currentValues, value);

    // console.log(
    //  name,
    //  currentValue,
    //  isEmpty,
    //  currentValues?.[name],
    //  value?.[name],
    // );

    if (
     !isEmpty &&
     (value?.operators?.[name] === '' || value?.operators?.[name] === undefined)
    ) {
     console.log('empty operator');
     methods.setValue(`operators[${name}]`, 'equals'); // warning
    } else if (isEmpty) {
     console.log('empty value', `operators[${name}]`);
     methods.setValue(`operators[${name}]`, '');
    } else {
     console.log(
      'not empty',
      `operators[${name}]`,
      value?.operators?.[name],
      value?.operators?.[name],
     );
     methods.setValue(
      `operators[${name}]`,
      value?.operators?.[name] || 'equals',
     );
    }
   }
  }, 300);

  const subscription = methods.watch(debouncedHandler);

  return () => {
   subscription.unsubscribe();
  };
 }, [methods.watch]);

 useEffect(() => {
  const vals = getValuesFromStorage();

  if (vals) {
   const output = {
    ...vals,
   };

   methods.reset(output);
  }
 }, [currentColumnsPreset, structure]);

 //  useEffect(() => {
 //   // how to prevent this from running on change from sidebar
 //   const unsubscribe = apiRef.current.subscribeEvent(
 //    'filterModelChange',
 //    (model) => {},
 //   );

 //   return () => {
 //    unsubscribe(); // Cleanup
 //   };
 //  }, [apiRef]);

 return {
  applyFilters,
  resetFilters,
  methods,
  columns,
  currentColumnsPreset,
  structure,
  isFormChanged,
 };
};

export default useFilterSidebar;
